/**
 * Add and edit member.
 *
 * The emergency contact is optional and takes any number (owner's direction,
 * 2 Oct 2026 — it used to be required, SPEC.md §4). A number that IS given
 * must be dialable and someone else's. A minor still needs a guardian.
 *
 * Email is required for a different reason: it is a login identity and the
 * only channel a forgotten password can be recovered through. An admin who
 * adds a member without one has created an account that can lock itself out
 * permanently, so the form does not allow it.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ageError,
  ageFrom,
  allowedTiersForAge,
  emergencyPhoneError,
  normalizeEmergencyPhone,
  feeFor,
  guardianPhoneError,
  findMemberByEmail,
  findMemberByPhone,
  normalizeEmail,
  normalizePhone,
  tierForDob,
  TIERS,
} from '../../data';
import { useClub } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { AnyPhoneInput, Button, Choice, Field, FormError, Hint, PhoneInput, Row2, SheetActions, SheetHead } from '../ui';

import { useFees, useGrounds } from '../../context/ClubContext';

// Props: { member?, onCreated?, nextBib }
//   member    — omit to add someone new.
//   onCreated — add flow only: called with the new member id so the caller can
//               offer a pay link. NOT called when the committee chose cash —
//               then the sheet goes straight to Membership requests itself.
export function MemberSheet({ member, onCreated, nextBib }) {
  const { addMember, db, updateMember } = useClub();
  const grounds = useGrounds();
  const fees = useFees();
  const { closeSheet, toast } = useUI();
  const navigate = useNavigate();
  const isEdit = !!member;
  /*
   * How the joining fee will come in (owner's direction, 2 Oct 2026): a
   * committee often takes cash at the ground. Cash skips the payment-link
   * screen and lands on Membership requests with this person at the top,
   * highlighted, where the cash is recorded and the membership activated.
   */
  const [payBy, setPayBy] = useState('link'); // 'link' | 'cash'

  const [form, setForm] = useState({
    name: member?.name ?? '',
    phone: member?.phone ?? '',
    email: member?.email ?? '',
    dob: member?.dob ?? '',
    gender: member?.gender ?? '',
    ground: member?.ground ?? Object.keys(grounds)[0] ?? '',
    tier: member?.tier ?? 'annual',
    blood: member?.blood ?? '',
    tshirt: member?.tshirt ?? '',
    emName: member?.emName ?? '',
    emPhone: member?.emPhone ?? '',
    guardianName: member?.guardianName ?? '',
    guardianPhone: member?.guardianPhone ?? '',
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  /*
   * Once the admin picks a tier by hand, the birth date stops overriding it.
   * Auto-selection is a convenience, not a policy — a student of 20 and a life
   * member of 40 are both older than 18, and silently resetting either back to
   * "annual" every time the date is edited would be the form arguing with the
   * person filling it in.
   */
  const [tierTouched, setTierTouched] = useState(isEdit);

  /** The club's grounds, in one stable order for the dropdown. */
  const groundKeys = Object.keys(grounds);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  /**
   * A new birth date re-derives the tier, unless the admin has overridden it.
   * One exception to that respect: if the date makes the hand-picked tier
   * one this age is not OFFERED (junior at 30, student at 40), it snaps to
   * the derived tier — an option that is about to vanish from the dropdown
   * must not stay silently selected underneath it.
   */
  function setDob(value) {
    setForm((f) => {
      const next = { ...f, dob: value };
      const derived = tierForDob(value);
      if (!tierTouched) {
        if (derived) next.tier = derived;
      } else if (!allowedTiersForAge(ageFrom(value || null)).includes(f.tier)) {
        next.tier = derived ?? 'annual';
      }
      return next;
    });
  }

  const age = ageFrom(form.dob || null);
  const isMinor = age !== null && age < 18;
  const dobProblem = ageError(form.dob || null);

  /*
   * Which tiers this age may be OFFERED: no junior for an adult, no student
   * past 25 (data/tier.js). Editing a legacy record whose stored tier the
   * rule would now hide still shows that tier — the dropdown reports what IS,
   * and hiding it would make the form lie about the record it is editing.
   */
  const tierOptions = allowedTiersForAge(age);
  if (!tierOptions.includes(form.tier)) tierOptions.unshift(form.tier);

  /*
   * Duplicate check, done HERE rather than by the admin searching the roster
   * first. Both are login identities and both are unique across live members
   * (CLAUDE.md §10.13), so a clash is not a warning — it is a save that would
   * be rejected, and finding that out on submit wastes the whole form.
   *
   * Editing skips the member's own record, or every edit would report the
   * person as a duplicate of themselves. Members who have left are skipped
   * too: their number is free to reissue, and only live members collide.
   */
  const otherMembers = (db?.members ?? []).filter(
    (m) => m.status !== 'left' && m.id !== member?.id,
  );
  const phoneClash = form.phone.trim() ? findMemberByPhone(otherMembers, form.phone) : null;
  const emailClash = normalizeEmail(form.email)
    ? findMemberByEmail(otherMembers, form.email)
    : null;

  async function save() {
    if (!form.name.trim()) return setError('Name is required.');
    if (!form.phone.trim()) return setError('Mobile number is required.');
    // The input holds +91 plus whatever has been typed so far — a short
    // number means they stopped mid-way, and the server would refuse it.
    if (!normalizePhone(form.phone)) {
      return setError('Enter the full 10-digit mobile number.');
    }
    if (phoneClash) {
      return setError(
        `That mobile number already belongs to ${phoneClash.name} (bib ${String(phoneClash.bib).padStart(3, '0')}).`,
      );
    }
    if (emailClash) {
      return setError(
        `That email address already belongs to ${emailClash.name} (bib ${String(emailClash.bib).padStart(3, '0')}).`,
      );
    }
    if (!normalizeEmail(form.email)) {
      return setError(
        'A valid email address is required — it is a sign-in identity and the only way this member can reset a password.',
      );
    }
    // Owner's direction, 17 Aug 2026: date of birth and gender are mandatory
    // on every member the committee creates. The DOB drives the tier gate and
    // the age on the record; neither can be guessed later.
    if (!form.dob) return setError('Date of birth is required.');
    if (!form.gender) return setError('Gender is required.');
    // Age gate. Checked before the safety fields so an under-age applicant is
    // told the real reason rather than being sent to fill in a guardian first.
    const dobError = ageError(form.dob || null);
    if (dobError) return setError(dobError);

    // The emergency contact is optional (owner, 2 Oct 2026) — but a number
    // that IS given must be dialable and must reach someone OTHER than the
    // member. See data/emergency.js (QA finding F-5).
    const emError = emergencyPhoneError(form.emPhone, form.phone);
    if (emError) return setError(emError);
    if (isMinor && !form.guardianName.trim()) {
      return setError('Guardian name is required for a minor.');
    }
    const guardianError = guardianPhoneError(form.guardianPhone);
    if (guardianError) return setError(guardianError);

    const draft = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: normalizeEmail(form.email),
      tier: form.tier,
      dob: form.dob,
      gender: form.gender, // 'M' | 'F' | 'X'
      ground: form.ground,
      blood: form.blood.trim() || null,
      tshirt: form.tshirt.trim() || null,
      emName: form.emName.trim() || null,
      emPhone: normalizeEmergencyPhone(form.emPhone),
      guardianName: form.guardianName.trim() || null,
      guardianPhone: normalizePhone(form.guardianPhone) || null,
    };

    setSaving(true);
    try {
      if (member) {
        await updateMember(member.id, draft);
        closeSheet();
        toast('Member updated');
      } else {
        const id = await addMember(draft);
        closeSheet();
        if (payBy === 'cash') {
          toast(`${draft.name.split(' ')[0]} added — record the cash, then activate`);
          navigate(`/a/requests?highlight=${encodeURIComponent(id)}&pay=cash`);
        } else {
          toast('Member added');
          onCreated?.(id);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setSaving(false);
    }
  }

  return (
    <>
      <SheetHead
        title={isEdit ? 'Edit member' : 'Add member'}
        sub={isEdit ? `Bib ${member.bib}` : `Bib ${nextBib} will be assigned automatically`}
      />

      <Field label="Full name" required>
        <input
          name="member-name"
          autoComplete="off"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g. Karthik Raja"
        />
      </Field>

      <Row2>
        <Field label="Mobile number" required>
          {/* Fixed +91 — every member is on an Indian mobile, so the ten
              digits are all the admin should ever have to type. */}
          <PhoneInput
            name="member-phone"
            value={form.phone}
            onChange={(v) => set('phone', v)}
          />
          {phoneClash && (
            <Hint tone="bad">
              Already on the roster — {phoneClash.name}, bib{' '}
              {String(phoneClash.bib).padStart(3, '0')}.
            </Hint>
          )}
        </Field>
        <Field label="Date of birth" required>
          <input type="date" value={form.dob} onChange={(e) => setDob(e.target.value)} />
        </Field>
      </Row2>

      {/*
        The age is shown, not just used. An admin typing a date has no way to
        check they hit the right decade, and the tier quietly changing
        underneath them would otherwise look like a bug. Errors are shown here
        too rather than waiting for Save — an under-age applicant should be
        obvious while the date is still on screen.
      */}
      {form.dob && (
        <Hint tone={dobProblem ? 'bad' : undefined}>
          {dobProblem ?? (
            <>
              Age <strong>{age}</strong>
              {age !== null && age < 18 ? ' · a minor, so a guardian is required' : ''}
              {!tierTouched && tierForDob(form.dob)
                ? ` · tier set to ${TIERS[tierForDob(form.dob)].label} — change it if that is wrong`
                : ''}
            </>
          )}
        </Hint>
      )}

      <Field label="Email address" required>
        <input
          type="email"
          name="member-email"
          /*
           * NOT autoComplete="email". This form captures another person's
           * address, so offering the signed-in admin's own is wrong every
           * time — and it silently filled one in during testing.
           */
          autoComplete="off"
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
          placeholder="member@email.com"
        />
        {emailClash && (
          <Hint tone="bad">
            Already on the roster — {emailClash.name}, bib{' '}
            {String(emailClash.bib).padStart(3, '0')}.
          </Hint>
        )}
      </Field>
      <Hint style={{ marginTop: -8, marginBottom: 14 }}>
        They sign in with <b>this address or their number</b> plus a password, and reset links go
        here. Without it they can be locked out for good.
      </Hint>

      <Row2>
        <Field label="Gender" required>
          <select value={form.gender} onChange={(e) => set('gender', e.target.value)}>
            <option value="">—</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="X">Other</option>
          </select>
        </Field>
        <Field label="Home ground">
          {/*
            "New…" is an option rather than a button beside the select. The
            admin is already in the dropdown looking for a ground that is not
            there, which is the exact moment they want to add it — a separate
            control somewhere else on the form would be found second.
          */}
          {/* The club's own grounds, and only those — the committee adds them
              under Settings (owner's direction, 2 Sep 2026). */}
          <select
            value={form.ground}
            onChange={(e) => set('ground', e.target.value)}
            disabled={groundKeys.length === 0}
          >
            {groundKeys.length === 0 && <option value="">No grounds yet</option>}
            {groundKeys.map((k) => (
              <option key={k} value={k}>
                {grounds[k]}
              </option>
            ))}
          </select>
        </Field>
      </Row2>

      {groundKeys.length === 0 && (
        <Hint tone="warn">
          This club has no home grounds yet. Add them under{' '}
          <b>Settings → Home grounds</b>, then come back.
        </Hint>
      )}

      <Field label="Membership tier" required>
        <select
          value={form.tier}
          onChange={(e) => {
            setTierTouched(true);
            set('tier', e.target.value);
          }}
        >
          {tierOptions.map((k) => (
            <option key={k} value={k}>
              {TIERS[k].label} — ₹{feeFor(fees, k, 'join')} to join
            </option>
          ))}
        </select>
        {age !== null && tierOptions.length < Object.keys(TIERS).length && (
          <Hint>
            {age >= 18 && 'Junior is for under-18s'}
            {age >= 18 && age > 25 ? ' · ' : ''}
            {age > 25 && 'Student is for 25 and under'} — not offered at age {age}.
          </Hint>
        )}
      </Field>

      <Row2>
        <Field label="Blood group">
          <input value={form.blood} onChange={(e) => set('blood', e.target.value)} placeholder="O+" />
        </Field>
        <Field label="T-shirt">
          <input value={form.tshirt} onChange={(e) => set('tshirt', e.target.value)} placeholder="M" />
        </Field>
      </Row2>

      <Row2>
        <Field label="Emergency contact">
          <input
            value={form.emName}
            onChange={(e) => set('emName', e.target.value)}
            placeholder="Name (optional)"
          />
        </Field>
        <Field label="Emergency phone">
          <AnyPhoneInput
            name="member-em-phone"
            value={form.emPhone}
            onChange={(v) => set('emPhone', v)}
          />
        </Field>
      </Row2>

      {isMinor && (
        <>
          <Row2>
            <Field label="Guardian name" required>
              <input
                value={form.guardianName}
                onChange={(e) => set('guardianName', e.target.value)}
              />
            </Field>
            <Field label="Guardian phone">
              <PhoneInput
                name="member-guardian-phone"
                value={form.guardianPhone}
                onChange={(v) => set('guardianPhone', v)}
              />
            </Field>
          </Row2>
          <Hint>Required for runners under 18.</Hint>
        </>
      )}

      {!isEdit && (
        <>
          <Field label="How will they pay?" required>
            <Choice
              name="member-pay-by"
              value={payBy}
              onChange={setPayBy}
              options={[
                { value: 'link', label: 'Payment link', note: 'Send them a one-time link to pay online' },
                { value: 'cash', label: 'Cash', note: 'You collect it — record it on the next screen' },
              ]}
            />
          </Field>
          <Hint style={{ marginTop: 2 }}>
            <b>Next step:</b> they go to <b>Membership requests</b>, not the members list.{' '}
            {payBy === 'cash'
              ? 'You land there with them at the top — record the cash, then activate the membership.'
              : 'Send the payment link, then activate the membership once they have paid.'}{' '}
            Only then are they on the roster.
          </Hint>
        </>
      )}

      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save} disabled={saving}>
          {isEdit ? 'Save changes' : 'Add member'}
        </Button>
      </SheetActions>
    </>
  );
}
